'use client';

import type { ReactNode } from 'react';
import { useMemo, useRef } from 'react';
import Link from 'next/link';
import { useElementWidth } from '@charts';
import type { Item } from './model';
import { collectItems } from './model';
import { approach } from './motion';
import type { Highlight } from './paint';
import { paintBand, paintBars } from './paint';
import { Chart } from './parts/Chart';
import { railMask, railSpan, tipPosition } from './rail';
import type { Stage } from './stages';
import { bucketizeAll } from './stages';
import styles from './ScrollyPulse.module.css';
import { useScrollFrame } from './useScrollFrame';

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

const TIP_TAU_MS = 260;
const CHART_TAU_MS = 260;

type Transition = { from: Stage; to: Stage; target: number };

const activeTransition = (items: Item[], line: number): Transition => {
  let current: Transition | null = null;
  for (const item of items) {
    if (item.kind === 'gate') {
      const node = item.el.querySelector('span');
      if (!node) continue;
      const c = node.getBoundingClientRect();
      const passed = line >= c.top + c.height / 2;
      if (passed || !current) {
        current = { from: item.from, to: item.to, target: passed ? 1 : 0 };
      }
      if (!passed) break;
    } else if (item.prev !== item.stage && !item.gated) {
      const r = item.el.getBoundingClientRect();
      if (r.top - 120 > line) break;
      current = { from: item.prev, to: item.stage, target: 1 };
    }
  }
  return current ?? { from: 'published', to: 'published', target: 1 };
};

const activeHighlight = (
  items: Item[],
  line: number
): Highlight | undefined => {
  let hl: Highlight | undefined;
  for (const item of items) {
    const r = item.el.getBoundingClientRect();
    if (r.top > line) break;
    hl =
      item.kind === 'section' && r.bottom >= line ? item.highlight : undefined;
  }
  return hl;
};

export function ScrollyPulse({
  intro,
  children,
  className,
}: {
  intro: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const chartRef = useRef<HTMLDivElement>(null);
  const barsRef = useRef<HTMLDivElement>(null);
  const bandRef = useRef<HTMLDivElement>(null);
  const topBlurRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const flowRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const railFillRef = useRef<HTMLDivElement>(null);

  const width = useElementWidth(chartRef);
  const n = Math.max(1, Math.floor(width / 2));
  const cols = useMemo(() => (width > 0 ? bucketizeAll(n) : null), [n, width]);

  const tip = useRef(0);
  const chart = useRef({ key: '', p: 1, lastY: 0, lastT: 0 });

  const frame = (): boolean => {
    const flowEl = flowRef.current;
    const railEl = railRef.current;
    if (!flowEl || !railEl || !barsRef.current || !cols) return false;

    const stuck = (sentinelRef.current?.getBoundingClientRect().top ?? 1) < 0;
    chartRef.current?.setAttribute('data-flipped', String(stuck));
    topBlurRef.current?.setAttribute('data-visible', String(stuck));

    const s = chart.current;
    const now = performance.now();
    const dt = Math.min(100, now - s.lastT);
    s.lastT = now;
    const reduce = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    const tau = (ms: number) => (reduce ? 0.001 : ms);

    const items = collectItems(flowEl);
    const flowTop = flowEl.getBoundingClientRect().top;
    const line = window.innerHeight / 2;
    const nodes = items
      .filter((item) => item.kind === 'gate')
      .flatMap((item) => [...item.el.querySelectorAll('span')]);
    const span = railSpan(flowTop, nodes);
    if (!span) return false;

    const textRects = items
      .filter((item) => item.kind === 'section')
      .map((item) => {
        const r = item.el.getBoundingClientRect();
        return { top: r.top - flowTop, bottom: r.bottom - flowTop };
      });
    const mask = railMask(span, textRects);
    const geometry = `${span.top}:${span.height}:${mask}`;
    if (railEl.dataset.geometry !== geometry) {
      railEl.dataset.geometry = geometry;
      railEl.style.top = `${span.top}px`;
      railEl.style.height = `${span.height}px`;
      railEl.style.maskImage = mask;
      railEl.style.webkitMaskImage = mask;
    }

    const target = tipPosition(line - flowTop, span);
    tip.current = approach(tip.current, target, dt, tau(TIP_TAU_MS), 0.5);
    const tipY = tip.current;
    const tipAbs = tipY + span.top + flowTop;
    if (railFillRef.current) {
      railFillRef.current.style.height = `${tipY}px`;
    }

    for (const item of items) {
      if (item.kind !== 'gate') continue;
      const r = item.el.getBoundingClientRect();
      const p = clamp01((tipAbs - r.top) / r.height);
      item.el.style.setProperty('--p', String(p));
      const [node, after] = item.el.querySelectorAll('span');
      // nodes light exactly when they cross mid-screen, independent of the
      // parallax-lagged fill
      for (const [el, flag] of [
        [node, 'reached'],
        [after, 'completed'],
      ] as const) {
        if (!el) continue;
        const c = el.getBoundingClientRect();
        item.el.dataset[flag] = String(line >= c.top + c.height / 2);
      }
    }

    const tr = activeTransition(items, line);
    const key = `${tr.from}->${tr.to}`;
    const y = document.body.scrollTop || window.scrollY;
    if (s.key !== key) {
      s.key = key;
      s.p = y >= s.lastY ? 0 : 1;
    }
    s.lastY = y;
    s.p = approach(s.p, tr.target, dt, tau(CHART_TAU_MS));

    const highlight = activeHighlight(items, line);
    paintBars(barsRef.current, cols, tr.from, tr.to, clamp01(s.p), highlight);
    if (bandRef.current) paintBand(bandRef.current, highlight);

    return s.p !== tr.target || tipY !== target;
  };

  useScrollFrame(frame, n);

  return (
    <>
      <div className={styles.topBlur} ref={topBlurRef} aria-hidden={true} />
      <Link href="/" className={styles.home}>
        ← Ondřej Bárta
      </Link>

      <div className={className}>
        {intro}
        <div ref={sentinelRef} aria-hidden={true} />

        <div className={styles.stickyScope}>
          <div className={styles.stickyChart}>
            <div className={styles.chartColumn}>
              <Chart
                chartRef={chartRef}
                barsRef={barsRef}
                bandRef={bandRef}
                width={width}
                n={n}
              />
            </div>
          </div>

          <div className={styles.flow} ref={flowRef}>
            <div className={styles.rail} ref={railRef} aria-hidden={true}>
              <div className={styles.railFill} ref={railFillRef} />
            </div>
            {children}
          </div>
        </div>
      </div>
    </>
  );
}
